import { useState } from "react";
import { Plus, Trash2, Pencil, ListChecks, BookOpen } from "lucide-react";
import { PhotoPicker } from "@/components/PhotoPicker";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Empty, Pill } from "@/components/kit";

const CATS = ["opening", "service", "cleaning", "closing", "cellar", "bar", "other"];
const blank = { name: "", category: "opening", due_time: "10:00", frequency: "daily", active: true, items: [{ label: "", kind: "tick", min: null, max: null }] };

function HowTo({ item, i, update }) {
  return (
    <div data-testid={`template-item-howto-${i}`} className="col-span-12 space-y-2 rounded-xl bg-slate-50 p-3">
      <textarea data-testid={`template-item-instruction-${i}`} rows={2} maxLength={600} className="ka-input" placeholder="How to do it, e.g. Probe the thickest part, wait 10 seconds, wipe the probe after" value={item.instruction || ""} onChange={(e) => update({ instruction: e.target.value })} />
      <input data-testid={`template-item-video-${i}`} type="url" pattern="https://.*" maxLength={300} className="ka-input" placeholder="Video link (optional), e.g. https://youtube.com/..." value={item.video_url || ""} onChange={(e) => update({ video_url: e.target.value })} />
      <PhotoPicker testid={`template-item-photo-${i}`} value={item.photo_id ? [item.photo_id] : []} onChange={(ids) => update({ photo_id: ids[ids.length - 1] || null })} />
    </div>
  );
}

function ItemRow({ item, i, update, remove }) {
  const [how, setHow] = useState(!!(item.instruction || item.photo_id || item.video_url));
  return (
    <div className="grid grid-cols-12 gap-2">
      <input data-testid={`template-item-label-${i}`} className="ka-input col-span-12 sm:col-span-6" placeholder="What needs checking?" value={item.label} onChange={(e) => update({ label: e.target.value })} />
      <select data-testid={`template-item-kind-${i}`} className="ka-input col-span-4 sm:col-span-2" value={item.kind} onChange={(e) => update({ kind: e.target.value })}>
        <option value="tick">Yes/No</option><option value="temp">Temp</option><option value="text">Note</option><option value="staff">Staff list</option>
      </select>
      <input type="number" step="any" disabled={item.kind !== "temp"} className="ka-input col-span-3 sm:col-span-2 disabled:opacity-40" placeholder="min" value={item.min ?? ""} onChange={(e) => update({ min: e.target.value === "" ? null : Number(e.target.value) })} />
      <input type="number" step="any" disabled={item.kind !== "temp"} className="ka-input col-span-3 sm:col-span-1 disabled:opacity-40" placeholder="max" value={item.max ?? ""} onChange={(e) => update({ max: e.target.value === "" ? null : Number(e.target.value) })} />
      <button type="button" onClick={remove} className="ka-press col-span-2 sm:col-span-1 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="mx-auto h-4 w-4" /></button>
      <button type="button" data-testid={`template-item-howto-toggle-${i}`} onClick={() => setHow(!how)} className="col-span-12 -mt-1 inline-flex items-center gap-1 justify-self-start text-xs font-semibold text-sky-700 hover:underline"><BookOpen className="h-3.5 w-3.5" />{how ? "Hide how-to" : "Add how-to for staff"}</button>
      {how && <HowTo item={item} i={i} update={update} />}
    </div>
  );
}

function TemplateForm({ form, setForm, onSave, busy }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setItem = (i, patch) => set("items", form.items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <form onSubmit={onSave} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Checklist name *"><input data-testid="template-name-input" required className="ka-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Category"><select data-testid="template-category-select" className="ka-input" value={form.category} onChange={(e) => set("category", e.target.value)}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Due by"><input data-testid="template-due-input" type="time" className="ka-input" value={form.due_time} onChange={(e) => set("due_time", e.target.value)} /></Field>
        <Field label="Frequency"><select className="ka-input" value={form.frequency} onChange={(e) => set("frequency", e.target.value)}><option value="daily">Daily</option><option value="weekly">Weekly (Mondays)</option></select></Field>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium text-slate-700">Items</div>
        {form.items.map((it, i) => <ItemRow key={i} item={it} i={i} update={(p) => setItem(i, p)} remove={() => set("items", form.items.filter((_, j) => j !== i))} />)}
        <button type="button" data-testid="template-add-item" onClick={() => set("items", [...form.items, { label: "", kind: "tick", min: null, max: null }])} className="ka-press text-sm font-semibold text-emerald-700 hover:underline">+ Add item</button>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <button data-testid="save-template-button" disabled={busy} className="ka-press rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">{busy ? "Saving…" : "Save checklist"}</button>
      </div>
    </form>
  );
}

export default function Templates() {
  const { venueId, venue } = useAuth();
  const { data, reload } = useApi(venueId ? "/check_templates" : null, { venue_id: venueId });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, ...rest } = form;
    const body = { name: rest.name, category: rest.category, due_time: rest.due_time, frequency: rest.frequency, active: rest.active, items: rest.items.filter((i) => i.label.trim()), venue_id: venueId };
    try {
      if (id) await api.put(`/check_templates/${id}`, body);
      else await api.post("/check_templates", body);
      toast.success("Checklist saved");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Past records are kept.`)) return;
    await api.delete(`/check_templates/${t.id}`);
    reload();
  };

  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Checklists setup" subtitle="Build venue-specific opening, cleaning, service and closing checks. Temperature items get their own limits."
        actions={<button data-testid="add-template-button" onClick={() => setForm({ ...blank })} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />New checklist</button>} />
      {data && !data.length && <Empty icon={ListChecks} title="No checklists yet" />}
      <div className="grid gap-4 md:grid-cols-2">
        {(data || []).map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="ka-card p-6">
            <div className="flex items-start gap-3">
              <div className="flex-1"><div className="ka-eyebrow">{t.category} · {t.frequency} · due {t.due_time}</div><div className="mt-1 font-display text-xl font-bold">{t.name}</div></div>
              {!t.active && <Pill status="low" label="Inactive" />}
              <button data-testid={`edit-template-${t.id}`} onClick={() => setForm(t)} className="ka-press rounded-md p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-template-${t.id}`} onClick={() => remove(t)} className="ka-press rounded-md p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-600">{t.items.map((i, k) => <li key={k}>• {i.label}{i.kind === "temp" && <span className="font-mono text-xs text-slate-400"> [{i.min ?? "–"}…{i.max ?? "–"}°C]</span>}</li>)}</ul>
          </div>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form?.id ? "Edit checklist" : "New checklist"}</DialogTitle></DialogHeader>
          {form && <TemplateForm form={form} setForm={setForm} onSave={save} busy={busy} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
